import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../redux/authSlice';
import { motion, AnimatePresence } from 'framer-motion';
import LoadingComman from '../LoadingComman/LoadingComman';
import useAxiosInstance from '../../services/useAxiosInstance';
import { LuLayoutDashboard } from "react-icons/lu";
import { CiLogout } from "react-icons/ci";
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

// Import all your existing icon components
import { AiOutlineMenuUnfold, AiOutlineStop, AiOutlineAudit } from "react-icons/ai";
import { IoIosContact, IoIosInformationCircleOutline } from "react-icons/io";
import { FaShareSquare } from "react-icons/fa";
import { FaQ, FaRegMessage } from "react-icons/fa6";
import { SiMarketo, SiAutodeskrevit, SiDocusaurus } from "react-icons/si";
import { MdOutlineAddHome, MdPayment, MdOutlineTabletAndroid, MdDonutSmall, MdContactMail, MdOutlineManageAccounts } from "react-icons/md";
import { TbFileReport, TbListDetails } from "react-icons/tb";
import { CiCircleAlert } from "react-icons/ci";
import { RiHome2Line, RiSurveyLine, RiCommunityLine } from "react-icons/ri";
import { IoPeopleOutline } from "react-icons/io5";
import { PiListStarLight, PiNewspaperClippingLight } from "react-icons/pi";
import { GrUserWorker } from "react-icons/gr";
import { FaCcAmazonPay } from "react-icons/fa";
import { FcFaq } from "react-icons/fc";
import { ContactRound, Route, UserPen } from 'lucide-react';

// import logoImg from "../../assets/Component 1.svg";
import logoImg from "../../assets/Logo.jpg";

// Map of icon components
const iconComponents = {
  AiOutlineMenuUnfold: <UserPen />,
  MdOutlineAddHome: <MdOutlineAddHome />,
  CiLogout: <CiLogout />,
  TbFileReport: <TbFileReport />,
  CiCircleAlert: <CiCircleAlert />,
  FaRegMessage: <FaRegMessage />,
  MdPayment: <MdPayment />,
  RiHome2Line: <RiHome2Line />,
  AiOutlineStop: <AiOutlineStop />,
  IoIosInformationCircleOutline: <MdOutlineManageAccounts />,
  MdOutlineTabletAndroid: <MdOutlineTabletAndroid />,
  RiSurveyLine: <RiSurveyLine />,
  IoPeopleOutline: <IoPeopleOutline />,
  IoIosContact: <IoIosContact />,
  FaShareSquare: <FaShareSquare />,
  SiMarketo: <ContactRound />,
  MdContactMail: <MdContactMail />,
  PiListStarLight: <Route />,
  RiCommunityLine: <RiCommunityLine />,
  AiOutlineAudit: <AiOutlineAudit />,
  TbListDetails: <TbListDetails />,
  GrUserWorker: <GrUserWorker />,
  MdDonutSmall: <MdDonutSmall />,
  SiAutodeskrevit: <SiAutodeskrevit />,
  SiDocusaurus: <SiDocusaurus />,
  FaCcAmazonPay: <FaCcAmazonPay />,
  PiNewspaperClippingLight: <PiNewspaperClippingLight />,
  FcFaq: <FaQ />,
  LuLayoutDashboard: <LuLayoutDashboard />,
};

// Tooltip component for collapsed mode
const Tooltip = ({ show, name, position }) => {
  if (!show) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: -5 }}
      animate={{ opacity: 1, x: 5, y: -40 }}
      className="fixed left-16 bg-blue-700 text-white py-1 px-3 rounded-md shadow-lg z-50 whitespace-nowrap"
    >
      <div className="absolute -left-1 top-1/2 transform -translate-x-1 -translate-y-1/2 border-4 border-transparent border-r-blue-700" />
      {name}
    </motion.div>
  );
};

const Sidebar = ({ isSidebarOpen, closeSidebar, isCollapsed: propIsCollapsed, toggleSidebar }) => {
  const roleId = useSelector(state => state.auth.roleId);
  const token = useSelector(state => state.auth.token);
  const dispatch = useDispatch();
  const location = useLocation();
  const axiosInstance = useAxiosInstance();

  const [rolePageData, setRolePageData] = useState([]);
  const [loadingComman, setLoadingComman] = useState(true);
  const [isMobileView, setIsMobileView] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  // Use the prop instead of local state for collapsed status
  const isCollapsed = propIsCollapsed;

  // Get current path for proper active state detection
  const currentPath = location.pathname;

  // Check for mobile view on component mount and window resize
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 768);
      console.log('mobile view:', window.innerWidth < 768);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  // Fetch role page data
  useEffect(() => {
    const fetchRolePageData = async () => {
      try {
        const response = await axiosInstance.get(
          `/api/public/rolepage/rolePages/access/${roleId}`
        )
        setRolePageData(response.data);
        // console.log('Role Page Data:', response.data);
      } catch (error) {
        if (error.response) {
          console.error('Server Response Error:', error.response.status, error.response.data);
        } else if (error.request) {
          console.error('No Response:', error.request);
        } else {
          console.error('Error:', error.message);
        }
      } finally {
        setLoadingComman(false);
      }
    };

    if (roleId) {
      fetchRolePageData();
    }
  }, [roleId, axiosInstance]);

  const handleLogout = () => {
    dispatch(logout());
  };

  // Helper function to determine if a link should be active
  const isLinkActive = (path) => {
    // Root path exact match
    if (path === '/' && currentPath === '/') {
      return true;
    }

    // Other paths should be active when currentPath starts with path (unless path is just '/')
    if (path !== '/' && currentPath.startsWith(path)) {
      return true;
    }

    return false;
  };

  // Animation variants
  const itemVariants = {
    expanded: { opacity: 1, x: 0 },
    collapsed: { opacity: 0, x: -10 }
  };

  const iconVariants = {
    expanded: { marginRight: '16px' },
    collapsed: { marginRight: '0px' }
  };

  // Make sure sidebar is visible in all states
  useEffect(() => {
    // Force links visibility fix
    if (isCollapsed) {
      const timer = setTimeout(() => {
        const links = document.querySelectorAll('.nav-link-icon');
        links.forEach(link => {
          link.style.opacity = '1';
          link.style.visibility = 'visible';
          link.style.display = 'block';
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isCollapsed]);

  const activeIndicatorVariants = {
    initial: { height: 0 },
    animate: { height: '100%', transition: { duration: 0.3 } }
  };

  if (loadingComman) {
    return <LoadingComman />;
  }

  // Define sidebar style to fix CSS conflicts
  const inlineStyles = `
    .sidebar-nav-link:hover {
      background-color: rgba(44, 60, 207, 0.7) !important;
    }
    .nav-link-icon {
      display: block !important;
      opacity: 1 !important;
      visibility: visible !important;
    }
    .sidebar-tooltip {
      z-index: 9999;
      background-color: #2563eb;
      color: white;
    }
    .active-nav-item {
      background-color: #1d4ed8 !important;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      position: relative;
    }
    .active-nav-item::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 4px;
      background-color: #93c5fd;
      border-radius: 0 3px 3px 0;
    }
  `;

  return (
    <>
      <style>{inlineStyles}</style>
      <div className="bg-gradient-to-b from-[#2731a3] to-[#1a2280] text-white h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-5 border-b border-blue-700 shrink-0">
          <AnimatePresence mode="wait">
            {(!isCollapsed || isMobileView) && (
              <motion.div
                className="flex items-center justify-center relative left-5 "
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <img src={logoImg} alt="Logo" style={{filter:"brightness(100)", scale:"3"}} className="h-8" />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Toggle button for sidebar collapse/expand */}
          {!isMobileView && (
            <motion.button
              onClick={toggleSidebar}
              className="p-2 rounded-full hover:bg-blue-700 transition-colors cursor-pointer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={isCollapsed ? "Expand" : "Collapse"}
            >
              {isCollapsed ? <FaChevronRight size={20} /> : <FaChevronLeft size={20} />}
            </motion.button>
          )}
          {isMobileView && (
            <motion.button
              onClick={() => closeSidebar && closeSidebar(true)}
              className="p-2  rounded-full hover:bg-blue-700 transition-colors cursor-pointer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Close sidebar"
            >
              <FaChevronLeft size={20} />
            </motion.button>
          )}
        </div>

        {/* Dashboard Link */}
        <div className="px-2 py-2">
          <NavLink
            to="/"
            exact
            className={({ isActive }) => `
              flex items-center px-3 py-3 rounded-lg transition-colors cursor-pointer relative overflow-hidden sidebar-nav-link
              ${isCollapsed ? "justify-center" : "justify-start"}
              ${isLinkActive('/') ? 'active-nav-item' : 'hover:bg-[#2c3ccf]'}
            `}
            onClick={() => {
              if (isMobileView && closeSidebar) closeSidebar();
            }}
          >
            <span
              className="text-lg text-blue-200 nav-link-icon"
              style={{ opacity: 1, visibility: 'visible', display: 'block' }}
            >
              <LuLayoutDashboard />
            </span>

            {(!isCollapsed || isMobileView) && (
              <span className="ml-3 text-blue-100">
                Dashboard
              </span>
            )}
          </NavLink>
        </div>

        {/* Menu Items from Role */}
        <nav className="flex-grow overflow-y-auto scrollbar-hide">
          <ul className="space-y-1 px-2 py-2">
            {rolePageData.map((item, index) => {
              // console.log(item.pageName)
              if (item.pageName === "Range List" || item.pageName === "Services ") return null
              return (
                <li key={item.pageId} className="relative">
                  <NavLink
                    to={item.componentUrl}
                    className={`
                      flex items-center px-3 py-3 rounded-lg transition-colors cursor-pointer relative overflow-hidden sidebar-nav-link
                      ${isCollapsed ? "justify-center" : "justify-start"}
                      ${isLinkActive(item.componentUrl) ? 'active-nav-item' : 'hover:bg-[#2c3ccf]'}
                    `}
                    onClick={() => {
                      if (isMobileView && closeSidebar) closeSidebar();
                    }}
                    onMouseEnter={() => setHoveredItem(item.pageId)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <span
                      className="text-lg text-blue-200 nav-link-icon"
                      style={{ opacity: 1, visibility: 'visible', display: 'block' }}
                    >
                      {iconComponents[item.pageIcon] || <LuLayoutDashboard />}
                      {/* {item.pageIcon } */}
                    </span>

                    {(!isCollapsed || isMobileView) && (
                      <span className="ml-3 text-blue-100">
                        {item.pageName}
                      </span>
                    )}

                    {/* Active indicator animation */}
                    {isLinkActive(item.componentUrl) && !isCollapsed && (
                      <motion.div
                        className="absolute left-0 top-0 w-1 bg-blue-300 rounded-r"
                        variants={activeIndicatorVariants}
                        initial="initial"
                        animate="animate"
                      />
                    )}
                  </NavLink>
                  {isCollapsed && !isMobileView && hoveredItem === item.pageId && (
                    <Tooltip show={true} name={item.pageName} position={`${2.5 + index * 3}rem`} />
                  )}
                </li>
              )
            })}
          </ul>
        </nav>

        {/* Logout Button */}
        <div className="border-t border-blue-700 pt-2 pb-4 shrink-0">
          <div
            className={`
              mx-2 flex items-center px-3 py-3 rounded-lg transition-colors cursor-pointer relative sidebar-nav-link
              ${isCollapsed ? "justify-center" : "justify-start"}
              hover:bg-[#2c3ccf]
            `}
            onClick={() => {
              handleLogout();
              if (isMobileView && closeSidebar) closeSidebar();
            }}
            onMouseEnter={() => setHoveredItem('logout')}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <motion.span
              className="text-lg text-blue-200 nav-link-icon"
              variants={iconVariants}
              initial={isCollapsed ? "collapsed" : "expanded"}
              animate={isCollapsed ? "collapsed" : "expanded"}
              style={{ opacity: 1, visibility: 'visible', display: 'block' }}
            >
              <CiLogout />
            </motion.span>
            <AnimatePresence mode="wait">
              {(!isCollapsed || isMobileView) && (
                <motion.span
                  className="ml-3 text-blue-100"
                  variants={itemVariants}
                  initial="collapsed"
                  animate="expanded"
                  exit="collapsed"
                  transition={{ duration: 0.2 }}
                >
                  Logout
                </motion.span>
              )}
            </AnimatePresence>
          </div>
          {isCollapsed && !isMobileView && hoveredItem === 'logout' && (
            <Tooltip show={true} name="Logout" position="2rem" />
          )}
        </div>

        {/* App version */}
        {(isCollapsed && !isMobileView) || isMobileView ? (
          <div className="text-xs text-blue-300 text-center py-2 opacity-50 shrink-0">
            v2.0
          </div>
        ) : null}
      </div>

      {/* Global style for hiding scrollbars */}
      <style jsx global>{`
        /* Hide scrollbar for Chrome, Safari and Opera */
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        /* Hide scrollbar for IE, Edge and Firefox */
        .scrollbar-hide {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
      `}</style>
    </>
  );
};

export default Sidebar;